import { useCallback, useEffect, useState } from "react";
import {
  ContactRound,
  Gauge,
  HardDrive,
  MessageSquare,
  Users,
  type LucideIcon,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import type { OrganizationUsageResponse } from "@multi-chat/shared";
import { isApiError } from "../../api/client.js";
import { getOrganizationUsage } from "../../api/organizations-api.js";
import { useOrganizationMessageBalance } from "../organizations/use-organization-message-balance.js";
import { LtrValue } from "../../components/common/LtrValue.js";
import { ProductErrorState } from "../../components/common/ProductErrorState.js";
import { formatStorageGigabyteAmount } from "./format-storage-gigabytes.js";
import { SettingsSectionPage } from "./SettingsSectionPage.js";
import { useSettingsWorkspace } from "./SettingsWorkspaceLayout.js";

type UsageLoadState =
  | { status: "loading" }
  | { status: "error" }
  | { status: "ready"; usage: OrganizationUsageResponse };

function isAbortError(error: unknown): boolean {
  return (
    (error instanceof DOMException && error.name === "AbortError") ||
    (error instanceof Error && error.name === "AbortError")
  );
}

function formatCount(language: string, value: number): string {
  return new Intl.NumberFormat(language).format(value);
}

function finiteProgress(used: number, limit: number): number {
  if (limit <= 0) return used > 0 ? 100 : 0;
  if (used >= limit) return 100;
  return Math.min(100, Math.round((used / limit) * 100));
}

function storageProgress(used: bigint, limit: bigint): number {
  if (limit <= 0n) return used > 0n ? 100 : 0;
  if (used >= limit) return 100;
  return Number((used * 100n) / limit);
}

function UsageProgress({
  percent,
  over,
  testId,
}: {
  percent: number;
  over: boolean;
  testId: string;
}): React.JSX.Element {
  return (
    <div className="grid gap-1.5">
      <div
        className="flex h-2 overflow-hidden rounded-full bg-[#132a27]/10"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={percent}
        data-testid={testId}
      >
        <div
          className={
            over
              ? "h-full rounded-full bg-warning transition-all duration-300"
              : "h-full rounded-full bg-[linear-gradient(90deg,#163a37_0%,#10b981_100%)] transition-all duration-300"
          }
          style={{ width: `${percent}%` }}
        />
      </div>
    </div>
  );
}

function UsageCard({
  title,
  testId,
  icon: Icon,
  progress,
  children,
}: {
  title: string;
  testId: string;
  icon: LucideIcon;
  progress?: React.ReactNode;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <section
      className="relative flex h-full flex-col gap-3.5 overflow-hidden rounded-2xl border border-line/70 bg-surface p-5 shadow-[0_8px_28px_-18px_rgb(19_42_39/0.12)] transition-shadow hover:shadow-[0_12px_32px_-16px_rgb(19_42_39/0.18)]"
      data-testid={testId}
    >
      <div className="flex items-center justify-between gap-2">
        <h2 className="m-0 text-[0.8125rem] font-semibold text-muted">{title}</h2>
        <span className="inline-grid size-8 shrink-0 place-items-center rounded-xl bg-[#132a27] text-[#34d399] ring-1 ring-[#34d399]/25">
          <Icon size={15} aria-hidden="true" />
        </span>
      </div>
      <div className="grid gap-2">{children}</div>
      {progress ? <div className="mt-auto pt-2">{progress}</div> : null}
    </section>
  );
}

function UsageSettingsSkeleton(): React.JSX.Element {
  return (
    <div
      className="mx-auto grid w-full max-w-[68rem] grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4"
      data-testid="settings-usage-loading"
      aria-busy="true"
    >
      {[0, 1, 2, 3].map((index) => (
        <div
          key={index}
          className="flex h-full flex-col gap-3.5 rounded-2xl border border-line/70 bg-surface p-5"
        >
          <div className="flex items-center justify-between gap-2">
            <div className="h-3.5 w-24 animate-pulse rounded bg-surface-muted" />
            <div className="size-8 animate-pulse rounded-xl bg-surface-muted" />
          </div>
          <div className="h-8 w-32 animate-pulse rounded bg-surface-muted" />
          <div className="mt-auto h-2 animate-pulse rounded-full bg-surface-muted" />
        </div>
      ))}
    </div>
  );
}

export function UsageSettingsView({
  state,
  onRetry,
  trailing,
}: {
  state: UsageLoadState;
  onRetry: () => void;
  trailing?: React.ReactNode;
}): React.JSX.Element {
  const { t, i18n } = useTranslation();

  if (state.status === "loading") {
    return <UsageSettingsSkeleton />;
  }

  if (state.status === "error") {
    return (
      <div className="mx-auto w-full max-w-[68rem]">
        <ProductErrorState
          testId="settings-usage-error"
          title={t("settings.usage.loadErrorTitle")}
          description={t("settings.usage.loadErrorBody")}
          onRetry={onRetry}
          retryLabel={t("settings.usage.retry")}
          retryTestId="settings-usage-retry"
        />
      </div>
    );
  }

  const { usage } = state;
  const language = i18n.language;
  const unlimited = t("settings.usage.unlimited");

  return (
    <div className="mx-auto grid w-full max-w-[68rem] grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
      <ContactsUsage usage={usage} language={language} unlimited={unlimited} />
      <TeamUsage usage={usage} language={language} unlimited={unlimited} />
      <StorageUsage usage={usage} unlimited={unlimited} />
      {trailing}
    </div>
  );
}

function countLabel(
  translate: (key: string, options?: Record<string, unknown>) => string,
  key: string,
  language: string,
  count: number,
): string {
  return translate(key, { count, formatted: formatCount(language, count) });
}

function UsageHeadline({
  primary,
  aside,
  asideWarning,
  testId,
  asideTestId,
}: {
  primary: string;
  aside: string;
  asideWarning: boolean;
  testId: string;
  asideTestId?: string;
}): React.JSX.Element {
  return (
    <div className="flex flex-col gap-1">
      <p
        className="m-0 text-[1.625rem] font-bold leading-tight tracking-[-0.025em] text-foreground tabular-nums"
        data-testid={testId}
      >
        <LtrValue>{primary}</LtrValue>
      </p>
      <p
        className={
          asideWarning
            ? "m-0 text-[0.75rem] font-semibold text-warning"
            : "m-0 text-[0.75rem] font-medium text-muted"
        }
        data-testid={asideTestId}
      >
        {aside}
      </p>
    </div>
  );
}

function ContactsUsage({
  usage,
  language,
  unlimited,
}: {
  usage: OrganizationUsageResponse;
  language: string;
  unlimited: string;
}): React.JSX.Element {
  const { t } = useTranslation();
  const { used, reserved, limit, available } = usage.contacts;
  const over = limit !== null && used > limit ? used - limit : 0;
  const primary =
    limit === null
      ? formatCount(language, used)
      : t("settings.usage.usedOfLimit", {
          used: formatCount(language, used),
          limit: formatCount(language, limit),
        });
  const aside =
    over > 0
      ? t("settings.usage.overLimit", { count: formatCount(language, over) })
      : limit === null
        ? unlimited
        : t("settings.usage.available", { count: formatCount(language, available ?? 0) });

  return (
    <UsageCard
      title={t("settings.usage.contacts")}
      testId="settings-usage-contacts"
      icon={ContactRound}
      progress={
        limit !== null ? (
          <UsageProgress
            percent={finiteProgress(used, limit)}
            over={over > 0}
            testId="settings-usage-contacts-progress"
          />
        ) : null
      }
    >
      <UsageHeadline
        primary={primary}
        aside={aside}
        asideWarning={over > 0}
        testId="settings-usage-contacts-amount"
        asideTestId={
          over > 0
            ? "settings-usage-contacts-over"
            : limit === null
              ? undefined
              : "settings-usage-contacts-available"
        }
      />
      {reserved > 0 ? (
        <p
          className="m-0 text-[0.75rem] text-muted"
          data-testid="settings-usage-contacts-reserved"
        >
          {t("settings.usage.reserved", { count: formatCount(language, reserved) })}
        </p>
      ) : null}
    </UsageCard>
  );
}

function TeamUsage({
  usage,
  language,
  unlimited,
}: {
  usage: OrganizationUsageResponse;
  language: string;
  unlimited: string;
}): React.JSX.Element {
  const { t } = useTranslation();
  const { occupied, members, pendingInvitations, limit } = usage.team;
  const over = limit !== null && occupied > limit ? occupied - limit : 0;
  const available = limit === null ? null : Math.max(0, limit - occupied);
  const primary =
    limit === null
      ? formatCount(language, occupied)
      : t("settings.usage.usedOfLimit", {
          used: formatCount(language, occupied),
          limit: formatCount(language, limit),
        });
  const aside =
    over > 0
      ? t("settings.usage.overLimit", { count: formatCount(language, over) })
      : available === null
        ? unlimited
        : t("settings.usage.available", { count: formatCount(language, available) });

  return (
    <UsageCard
      title={t("settings.usage.team")}
      testId="settings-usage-team"
      icon={Users}
      progress={
        limit !== null ? (
          <UsageProgress
            percent={finiteProgress(occupied, limit)}
            over={over > 0}
            testId="settings-usage-team-progress"
          />
        ) : null
      }
    >
      <UsageHeadline
        primary={primary}
        aside={aside}
        asideWarning={over > 0}
        testId="settings-usage-team-amount"
        asideTestId={over > 0 ? "settings-usage-team-over" : undefined}
      />
      <div className="mt-1 flex flex-wrap items-center gap-2 border-t border-line/50 pt-2">
        <p
          className="m-0 text-[0.72rem] text-muted"
          data-testid="settings-usage-team-members"
        >
          {countLabel(t, "settings.usage.members", language, members)}
        </p>
        <span className="text-line-strong">•</span>
        <p
          className="m-0 text-[0.72rem] text-muted"
          data-testid="settings-usage-team-pending"
        >
          {countLabel(t, "settings.usage.pendingInvitations", language, pendingInvitations)}
        </p>
      </div>
    </UsageCard>
  );
}

function StorageUsage({
  usage,
  unlimited,
}: {
  usage: OrganizationUsageResponse;
  unlimited: string;
}): React.JSX.Element {
  const { t } = useTranslation();
  const usedBytes = BigInt(usage.storage.usedBytes);
  const limitBytes =
    usage.storage.limitBytes === null ? null : BigInt(usage.storage.limitBytes);
  const overBytes =
    limitBytes !== null && usedBytes > limitBytes ? usedBytes - limitBytes : 0n;
  const gigabytes = (bytes: bigint) =>
    t("settings.usage.gigabytes", { amount: formatStorageGigabyteAmount(bytes) });
  const primary =
    limitBytes === null
      ? gigabytes(usedBytes)
      : t("settings.usage.usedOfLimit", {
          used: gigabytes(usedBytes),
          limit: gigabytes(limitBytes),
        });
  const aside =
    overBytes > 0n
      ? t("settings.usage.overLimit", { count: gigabytes(overBytes) })
      : limitBytes === null
        ? unlimited
        : t("settings.usage.available", {
            count: gigabytes(limitBytes > usedBytes ? limitBytes - usedBytes : 0n),
          });

  return (
    <UsageCard
      title={t("settings.usage.storage")}
      testId="settings-usage-storage"
      icon={HardDrive}
      progress={
        limitBytes !== null ? (
          <UsageProgress
            percent={storageProgress(usedBytes, limitBytes)}
            over={overBytes > 0n}
            testId="settings-usage-storage-progress"
          />
        ) : null
      }
    >
      <UsageHeadline
        primary={primary}
        aside={aside}
        asideWarning={overBytes > 0n}
        testId="settings-usage-storage-amount"
        asideTestId={overBytes > 0n ? "settings-usage-storage-over" : undefined}
      />
    </UsageCard>
  );
}

function formatCredits(value: string, language: string): string {
  try {
    return new Intl.NumberFormat(language).format(BigInt(value));
  } catch {
    return value;
  }
}

function TemplateMessagesCard(): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const { organizationId, role } = useSettingsWorkspace();
  const { balance, loading, error } = useOrganizationMessageBalance(organizationId, role);

  if (loading) {
    return (
      <section
        className="h-40 animate-pulse rounded-2xl border border-line/70 bg-surface"
        data-testid="settings-usage-template-messages-loading"
        aria-busy="true"
      />
    );
  }

  if (error || !balance) {
    return (
      <UsageCard
        title={t("settings.usage.templateMessages")}
        testId="settings-usage-template-messages"
        icon={MessageSquare}
      >
        <p className="m-0 text-[0.8125rem] text-muted">
          {t("settings.usage.creditsLoadError")}
        </p>
      </UsageCard>
    );
  }

  return (
    <UsageCard
      title={t("settings.usage.templateMessages")}
      testId="settings-usage-template-messages"
      icon={MessageSquare}
    >
      <UsageHeadline
        primary={
          balance.isUnlimited
            ? t("messageBalance.unlimited")
            : formatCredits(balance.availableCredits, i18n.language)
        }
        aside={
          balance.isUnlimited
            ? t("messageBalance.unlimitedMessaging")
            : t("messageBalance.availableCredits")
        }
        asideWarning={false}
        testId="settings-usage-template-messages-amount"
      />
      <div className="mt-auto border-t border-line/50 pt-2">
        <p
          className="m-0 text-[0.72rem] text-muted"
          data-testid="settings-usage-template-messages-reserved"
        >
          {t("settings.usage.creditsReserved")}:{" "}
          <LtrValue>{formatCredits(balance.reservedCredits, i18n.language)}</LtrValue>
        </p>
      </div>
    </UsageCard>
  );
}

export function UsageSettingsPage(): React.JSX.Element {
  const { t } = useTranslation();
  const { organizationId } = useSettingsWorkspace();
  const [state, setState] = useState<UsageLoadState>({ status: "loading" });
  const [loadNonce, setLoadNonce] = useState(0);
  const retry = useCallback(() => setLoadNonce((value) => value + 1), []);

  useEffect(() => {
    const controller = new AbortController();
    setState({ status: "loading" });
    getOrganizationUsage(organizationId, controller.signal)
      .then((usage) => {
        if (!controller.signal.aborted) setState({ status: "ready", usage });
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted || isAbortError(error)) return;
        if (!isApiError(error) && !(error instanceof Error)) return;
        setState({ status: "error" });
      });
    return () => controller.abort();
  }, [organizationId, loadNonce]);

  return (
    <SettingsSectionPage
      testId="settings-usage-page"
      icon={Gauge}
      title={t("settings.usage.title")}
      description={t("settings.usage.description")}
    >
      <UsageSettingsView
        state={state}
        onRetry={retry}
        trailing={<TemplateMessagesCard />}
      />
    </SettingsSectionPage>
  );
}
